import { useAuth } from '@payflip/web/auth';
import { loadScript } from '@payflip/web/utils';
import { useEffect, useState } from 'react';
import { shutdownIntercom } from './shutdown-intercom';

export type MaybeWindowWithIntercom = typeof window & {
  Intercom: (...args: unknown[]) => void;
};

export type IntercomCompany = {
  company_id: string;
  name: string;
  csm: string;
  onboarding_status: string;
};

export type IntercomConfig = {
  appId: string;
  restApiUri: string;
  userId: string;
  username: string;
  userLng: string;
  userEmail?: string;
  companies?: IntercomCompany[];
  isAdmin?: boolean;
};

type HookArgs = {
  config: IntercomConfig;
  skip?: boolean;
};

export type IntercomState = {
  loading: boolean;
  ready: boolean;
  error: Error | null;
};

export function useBootIntercom({
  skip,
  config: {
    userLng,
    appId,
    restApiUri,
    userId,
    username,
    companies,
    userEmail,
    isAdmin
  }
}: HookArgs): IntercomState {
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  const [state, setState] = useState<IntercomState>({
    loading: true,
    ready: false,
    error: null
  });
  const { getAccessToken, isAuthenticated, auth0User } = useAuth();
  const userLoggedOut = !isAuthenticated && state.ready;

  // Load Intercom script.
  useEffect(() => {
    void (async () => {
      if (appId === 'false') {
        return;
      }

      try {
        await loadScript(`https://widget.intercom.io/widget/${appId}`);
        setScriptLoaded(true);
      } catch (_err) {
        const error = _err as Error;
        setState({ loading: false, ready: false, error });
      }
    })();
  }, [appId]);

  // Shutdown Intercom when user logs out.
  useEffect(() => {
    if (userLoggedOut) {
      setState({ loading: false, ready: false, error: null });
      shutdownIntercom();
    }
  }, [userLoggedOut]);

  // Try to boot Intercom.
  useEffect(() => {
    void (async () => {
      if (appId === 'false') {
        return;
      }
      if (skip || !scriptLoaded || !isAuthenticated || !auth0User) {
        return;
      }

      const token = (await getAccessToken()) ?? '';

      if ((window as MaybeWindowWithIntercom)?.Intercom) {
        try {
          const { userHash } = await fetch(`${restApiUri}/intercom/user-hash`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`
            }
          }).then<{ userHash: string }>((r) => r.json());

          (window as MaybeWindowWithIntercom)?.Intercom('boot', {
            app_id: appId,
            api_base: 'https://api-iam.eu.intercom.io',
            user_id: userId,
            user_hash: userHash,
            name: username,
            tool_language: userLng,
            ...(typeof isAdmin === 'boolean' ? { is_admin: isAdmin } : {}),
            ...(userEmail ? { email: userEmail } : {}),
            ...(companies?.length ? { companies: companies } : {})
          });

          setState({ loading: false, ready: true, error: null });

          // Error handling.
        } catch (_err) {
          const error = _err as Error;
          setState({ loading: false, ready: false, error });
        }
      }
    })();
  }, [
    getAccessToken,
    skip,
    appId,
    restApiUri,
    userId,
    username,
    userLng,
    userEmail,
    companies,
    isAdmin,
    scriptLoaded,
    isAuthenticated,
    auth0User
  ]);

  return state;
}
